import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../components/Loader";
import { resetPassword } from "../../api/auth";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import signUpLayer from "../../assets/sign_up_layer.png";
import { Eye, EyeOff } from "lucide-react";

type ResetFormData = {
  newPassword: string;
  confirmPassword: string;
};

const ResetPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [confirmshowPassword, setConfirmShowPassword] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetFormData>();

  const onSubmit = async (data: ResetFormData) => {
    const email = localStorage.getItem("email"); // 👈 email from LS
    if (!email) {
      toast.error("Email not found. Please try again.");
      return;
    }

    if (data.newPassword !== data.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        email: email,
        newPassword: data.newPassword,
      };

      const res = await resetPassword(payload);
      // toast.success(res.data.message || "Password reset successfully!");
      // navigate("/login");
      toast.success(res.data.message || "Password reset successfully!", {
        onClose: () => {
          navigate("/login");
        },
      });

    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-center bg-cover p-6"
      style={{ backgroundImage: `url(${signUpLayer})` }}
    >
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      {/* ✅ Logo above card */}
      <div className="flex justify-center mb-3">
        <img src={logo} alt="Orthodontic Logo" className="h-24 w-auto object-contain" />
      </div>
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow">

        <h2 className="brand-heading text-center mb-6">
          Reset Password
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* New Password */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                {...register("newPassword", {
                  required: "New password is required",
                })}
                className="w-full pl-4 pr-11 py-2 border rounded focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-sm">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <div className="relative">
              <input
                type={confirmshowPassword ? "text" : "password"}
                placeholder="Confirm Password"
                {...register("confirmPassword", {
                  required: "Confirm password is required",
                  validate: (value) =>
                    value === watch("newPassword") || "Passwords do not match",
                })}
                className="w-full pl-4 pr-11 py-2 border rounded focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setConfirmShowPassword(!confirmshowPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {confirmshowPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className="w-full bg-[#016683] text-white py-2 rounded hover:opacity-90 transition"
          >
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
